import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { UserCog } from "lucide-react";

export default function PortalSwitch() {
  const { impersonation, startImpersonation, stopImpersonation } = useAuth();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/roles").then(r => setRoles(r.data));
    api.get("/users").then(r => setUsers(r.data));
  }, []);

  const start = async (target) => {
    try {
      const r = await startImpersonation(target);
      toast.success(`View As active: ${r.label}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell
      title="Owner Portal Switcher"
      subtitle="Non-silent impersonation · every request tagged in Audit Ledger"
    >
      {/* R1c dead-click fix F1+F2 (Round 2) — inline banner removed.
          The global <ImpersonationBanner /> mounted in App.js above <Routes>
          now owns `portal-current-impersonation` + `portal-stop-impersonation`
          so no page-local duplicate testids exist. `impersonation` state is
          still read by the picker so we could show contextual copy here in
          the future, but no banner is emitted from this page anymore. */}
      {impersonation && (
        <div
          className="mb-6 border border-erf-gold/40 bg-erf-gold/10 text-erf-dark rounded-md px-4 py-3 font-nav text-sm flex items-center justify-between"
          data-testid="portal-switch-current-context"
        >
          <div>Currently viewing as: <strong>{impersonation.label}</strong></div>
          <Button
            size="sm"
            variant="outline"
            onClick={stopImpersonation}
            data-testid="portal-switch-exit-inline"
          >
            Exit view-as
          </Button>
        </div>
      )}

      <div className="mb-8">
        <div className="font-display font-semibold text-erf-dark mb-3">View as role (role-only preview)</div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {roles.filter(r => r.key !== "OWNER_SUPER_ADMIN").map((r) => (
            <Card key={r.key} className="border-erf-borderCream" data-testid={`impersonate-role-card-${r.key}`}>
              <CardContent className="p-4">
                <div className="font-display font-semibold text-erf-dark">{r.label}</div>
                <div className="text-[11px] font-mono text-erf-dark/60 mt-0.5">{r.key}</div>
                <div className="text-xs mt-2 text-erf-dark/70 line-clamp-2 font-nav">{r.description}</div>
                <Button size="sm" onClick={() => start(`role:${r.key}`)} className="mt-3 bg-erf-dark text-erf-cream" data-testid={`impersonate-role-btn-${r.key}`}>
                  <UserCog className="w-4 h-4 mr-1" /> View as role
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="font-display font-semibold text-erf-dark mb-3">View as specific user</div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {users.filter(u => u.role_key !== "OWNER_SUPER_ADMIN").map((u) => (
            <Card key={u.id} className="border-erf-borderCream" data-testid={`impersonate-user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="font-display font-semibold text-erf-dark">{u.display_name}</div>
                <div className="text-[11px] font-mono text-erf-dark/60 mt-0.5">{u.email}</div>
                <div className="text-[10px] font-mono mt-1 uppercase tracking-widest text-erf-dark/50">{u.role_key}</div>
                <Button size="sm" onClick={() => start(`user:${u.id}`)} className="mt-3 bg-erf-dark text-erf-cream" data-testid={`impersonate-user-btn-${u.id}`}>
                  <UserCog className="w-4 h-4 mr-1" /> View as user
                </Button>
              </CardContent>
            </Card>
          ))}
          {users.filter(u => u.role_key !== "OWNER_SUPER_ADMIN").length === 0 && (
            <div className="text-sm text-erf-dark/60 font-nav">No non-owner users seeded yet. Create one in <em>/admin/users</em>.</div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
